import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

/**
 * Hook to check if current user has Owner role in the current clinic's customer context
 * Owner role allows full access to all features regardless of permission templates
 */
export function useIsOwner() {
  return useQuery({
    queryKey: ["is-owner"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return false;

      // Get current clinic to find customer context
      const { data: profile } = await supabase
        .from("profiles")
        .select("current_clinic_id")
        .eq("id", user.id)
        .single();

      if (!profile?.current_clinic_id) {
        return false; // No clinic selected, cannot be owner
      }

      // Get customer_id from clinic
      const { data: clinic } = await supabase
        .from("clinics")
        .select("customer_id")
        .eq("id", profile.current_clinic_id)
        .single();

      const customerId = clinic?.customer_id;
      if (!customerId) {
        return false;
      }

      // Check if user has Owner role for this customer
      const { data: userAccess, error } = await supabase
        .from("user_customer_access")
        .select(`
          customer_role:customer_roles(value)
        `)
        .eq("user_id", user.id)
        .eq("customer_id", customerId)
        .single();

      if (error || !userAccess) {
        return false;
      }

      // Check if the role is 'owner'
      const roleValue = (userAccess as any)?.customer_role?.value;
      return roleValue === "owner";
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });
}
